import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { AlertCircle, CalendarClock, ClipboardList, Copy, Edit3, FileText, Headphones, Mail, MessageSquarePlus, Phone, Plus, RefreshCw, Save, Search, ShoppingBag, UserRound, X } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import StoreLayout from "./StoreLayout";
import { copyPhoneNumber } from "./phoneActions";
import { preferredStoreLocationId, rememberedStoreLocationId } from "./storeLocationPreference";
import {
  StoreApiError,
  StoreCustomerDetail,
  StoreCustomerOwnedProduct,
  StoreCustomerOrder,
  StoreCustomerPayload,
  StoreCustomerSummary,
  StorePaginationMeta,
  StoreReservation,
  StoreReservationInteraction,
  StoreServiceRecord,
  StoreUser,
  addStoreCustomerInteraction,
  createStoreCustomer,
  getCurrentStoreUser,
  getStoreCustomer,
  logoutStore,
  searchStoreCustomers,
  updateStoreCustomer,
} from "./api";

const defaultPagination: StorePaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };

const statusLabels: Record<string, string> = {
  New: "新預約",
  Contacted: "已聯絡",
  Booked: "已預約",
  Confirmed: "已確認",
  Arrived: "已到店",
  Completed: "已完成",
  FollowUp: "需回訪",
  NoShow: "未到",
  Cancelled: "已取消",
  Closed: "已結案",
  Pending: "待處理",
  Processing: "處理中",
  Shipped: "已出貨",
  Unpaid: "未付款",
  Paid: "已付款",
  BalanceDue: "尾款待收",
  PendingHeadquarters: "待總部確認",
  PendingEngineering: "待工程部備貨",
  WaitingPurchase: "待廠商採購",
  PreparingOutbound: "待出庫",
  ShippedToStore: "已出庫到店",
  ReadyForPickup: "可通知取件",
};

const serviceTypeLabels: Record<string, string> = {
  HearingConsult: "聽力諮詢",
  HearingTest: "聽力檢測",
  TrialFitting: "試戴調整",
  Maintenance: "保養維修",
  FollowUp: "回訪追蹤",
};

const genderLabels: Record<string, string> = {
  Female: "女",
  Male: "男",
  Other: "其他",
};

const emptyCustomerForm: StoreCustomerPayload = {
  LocationId: "",
  Name: "",
  Phone: "",
  Email: "",
  Gender: "",
  Birthday: "",
  AddressCity: "",
  AddressDistrict: "",
  AddressPostalCode: "",
  AddressLine: "",
  Note: "",
};

function money(value?: number | null) {
  return `NT$ ${Number(value || 0).toLocaleString()}`;
}

function earSideLabel(value?: string | null) {
  const labels: Record<string, string> = {
    Left: "左耳",
    Right: "右耳",
    Both: "雙耳",
    Accessory: "配件",
  };
  return value ? labels[value] || value : "-";
}

function InfoTile({ icon, label, value, tone = "default" }: { icon?: ReactNode; label: string; value: ReactNode; tone?: "default" | "notice" }) {
  return (
    <div className={`min-w-0 rounded-md border px-3 py-2 ${tone === "notice" ? "border-[#b9dec5] bg-[#f0faf4]" : "border-[#edf0ee] bg-[#fbfcfb]"}`}>
      <p className="flex min-w-0 items-center gap-1.5 text-xs font-bold text-[#7b877f]">{icon}{label}</p>
      <div className="mt-1 min-w-0 break-all text-sm font-bold leading-6 text-[#203d32]">{value || "-"}</div>
    </div>
  );
}

function ActionButton({ children, href, onClick, variant = "primary" }: { children: ReactNode; href?: string; onClick?: () => void; variant?: "primary" | "secondary" }) {
  const className = `inline-flex h-11 min-w-[132px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 text-sm font-bold ${
    variant === "primary" ? "bg-[#315746] text-white hover:bg-[#254638]" : "border border-[#cfd8d2] bg-white text-[#315746] hover:bg-[#f0f5f2]"
  }`;
  if (href) return <Link className={className} to={href}>{children}</Link>;
  return <button className={className} onClick={onClick} type="button">{children}</button>;
}

function CustomerCard({ item, selected }: { item: StoreCustomerSummary; selected: boolean }) {
  return (
    <Link
      className={`block rounded-md border bg-white p-4 transition hover:border-[#b8c8bf] hover:shadow-[0_8px_22px_rgba(32,61,50,.08)] ${
        selected ? "border-[#9ac8aa] ring-2 ring-[#9ac8aa]/20" : "border-[#dfe6e1]"
      }`}
      to={`/store/customers/${encodeURIComponent(item.CustomerKey)}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-base font-extrabold text-[#203d32]">{item.Name}</p>
          <p className="mt-1 text-sm font-bold text-[#59665f]">{item.Phone}</p>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${item.IsMember ? "bg-[#fff2d8] text-[#8a4c00]" : "bg-[#e9f4ec] text-[#245438]"}`}>
          {item.IsMember ? "系統會員" : `${item.ReservationCount} 預約`}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs font-bold text-[#59665f]">
        <span className="rounded bg-[#f4f6f5] px-2 py-1">訂單 {item.OrderCount}</span>
        <span className="rounded bg-[#f4f6f5] px-2 py-1">互動 {item.InteractionCount}</span>
        <span className="rounded bg-[#f4f6f5] px-2 py-1">{item.LastLocationName || "門市"}</span>
      </div>
    </Link>
  );
}

function ReservationRow({ item }: { item: StoreReservation }) {
  return (
    <Link className="block rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3 transition hover:border-[#b8c8bf]" to={`/store/reservations/${encodeURIComponent(item.SourceType)}/${encodeURIComponent(item.SourceId)}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-extrabold text-[#203d32]">{item.Title}</p>
          <p className="mt-1 text-sm text-[#59665f]">{item.AppointmentDate || item.CreatedAt || "未指定日期"} {item.AppointmentTime || ""}</p>
        </div>
        <span className="rounded-full bg-[#eef3f8] px-2.5 py-1 text-xs font-bold text-[#255071]">{statusLabels[item.Status] || item.Status}</span>
      </div>
      {item.ProductName && <p className="mt-2 text-sm text-[#7b877f]">{item.ProductName}</p>}
    </Link>
  );
}

function OrderRow({ item }: { item: StoreCustomerOrder }) {
  return (
    <Link className="block rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3 transition hover:border-[#b8c8bf]" to={`/store/orders/${encodeURIComponent(item.OrderId)}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-extrabold text-[#203d32]">{item.OrderNumber}</p>
          <p className="mt-1 text-sm text-[#59665f]">{item.PickupLocationName || "取貨門市"} / {item.CreatedAt}</p>
        </div>
        <span className="shrink-0 rounded-full bg-[#f4f6f5] px-2.5 py-1 text-xs font-bold text-[#59665f]">{item.OrderStatusLabel || statusLabels[item.OrderStatus] || item.OrderStatus}</span>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <InfoTile label="商品" value={item.ProductSummary || "未帶商品"} />
        <InfoTile label="成交總額" value={money(item.TotalAmount)} />
        <InfoTile label="尾款" value={money(item.BalanceAmount)} tone={Number(item.BalanceAmount || 0) > 0 ? "notice" : "default"} />
      </div>
    </Link>
  );
}

function OwnedProductRow({ item }: { item: StoreCustomerOwnedProduct }) {
  const href = item.StoreSalesOrderId ? `/store/orders/${encodeURIComponent(item.StoreSalesOrderId)}` : "/store/orders";
  return (
    <Link className="block rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3 transition hover:border-[#b8c8bf]" to={href}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="break-words font-extrabold text-[#203d32]">
            {item.ProductName}{item.VariantLabel ? ` / ${item.VariantLabel}` : ""}
          </p>
          <p className="mt-1 text-sm font-bold text-[#59665f]">{item.OrderNumber || "門市訂單"} / {item.LocationName || "門市"}</p>
        </div>
        <span className="shrink-0 rounded-full bg-[#e9f4ec] px-2.5 py-1 text-xs font-bold text-[#245438]">{item.Status === "Active" ? "使用中" : item.Status}</span>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <InfoTile label="SKU" value={item.Sku || "-"} />
        <InfoTile label="機身序號" value={item.SerialNumber || "-"} />
        <InfoTile label="耳側" value={earSideLabel(item.EarSide)} />
        <InfoTile label="保固起算" value={item.WarrantyStartDate || "-"} />
      </div>
      <p className="mt-2 text-xs font-bold text-[#7b877f]">交付日期：{item.DeliveredAt || "-"}</p>
    </Link>
  );
}

function ServiceRecordRow({ item }: { item: StoreServiceRecord }) {
  return (
    <Link className="block rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3 transition hover:border-[#b8c8bf]" to={`/store/service-records/${encodeURIComponent(item.ServiceRecordId)}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-extrabold text-[#203d32]">{item.ServiceTypeLabel || serviceTypeLabels[item.ServiceType] || item.ServiceType}</p>
          <p className="mt-1 text-sm text-[#59665f]">{item.VisitDate} / {item.StoreUserName || "門市同仁"}</p>
        </div>
        <span className="shrink-0 rounded-full bg-[#e9f4ec] px-2.5 py-1 text-xs font-bold text-[#245438]">{item.LocationName || "門市"}</span>
      </div>
      {(item.Summary || item.TrialProductName) && (
        <p className="mt-2 line-clamp-2 text-sm leading-6 text-[#59665f]">{item.Summary || item.TrialProductName}</p>
      )}
      {item.NextFollowUpAt && <p className="mt-2 text-xs font-bold text-[#8a6a2f]">下次回訪：{item.NextFollowUpAt}</p>}
    </Link>
  );
}

export default function StoreCustomers() {
  const navigate = useNavigate();
  const { customerKey } = useParams();
  const [user, setUser] = useState<StoreUser | null>(null);
  const [currentLocationId, setCurrentLocationId] = useState<string | null>(() => rememberedStoreLocationId());
  const [customers, setCustomers] = useState<StoreCustomerSummary[]>([]);
  const [pagination, setPagination] = useState<StorePaginationMeta>(defaultPagination);
  const [keyword, setKeyword] = useState("");
  const [customer, setCustomer] = useState<StoreCustomerDetail | null>(null);
  const [reservations, setReservations] = useState<StoreReservation[]>([]);
  const [orders, setOrders] = useState<StoreCustomerOrder[]>([]);
  const [ownedProducts, setOwnedProducts] = useState<StoreCustomerOwnedProduct[]>([]);
  const [serviceRecords, setServiceRecords] = useState<StoreServiceRecord[]>([]);
  const [interactions, setInteractions] = useState<StoreReservationInteraction[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [body, setBody] = useState("");
  const [nextFollowUpAt, setNextFollowUpAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [customerFormOpen, setCustomerFormOpen] = useState(false);
  const [customerFormMode, setCustomerFormMode] = useState<"create" | "edit">("create");
  const [customerForm, setCustomerForm] = useState<StoreCustomerPayload>(emptyCustomerForm);
  const [customerSaving, setCustomerSaving] = useState(false);

  const effectiveLocationId = useMemo(() => (
    preferredStoreLocationId(user, currentLocationId)
  ), [currentLocationId, user]);

  async function loadList(locationId?: string | null, page = pagination.Page) {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentStoreUser();
      setUser(me.User);
      const nextLocationId = preferredStoreLocationId(me.User, locationId || currentLocationId);
      setCurrentLocationId(nextLocationId);
      const data = await searchStoreCustomers({ LocationId: nextLocationId, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage });
      setCustomers(data.Customers);
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "客戶查詢載入失敗。");
      if (error instanceof StoreApiError && error.status === 401) navigate("/store-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail() {
    if (!customerKey) {
      setCustomer(null);
      setReservations([]);
      setOrders([]);
      setOwnedProducts([]);
      setServiceRecords([]);
      setInteractions([]);
      return;
    }
    setDetailLoading(true);
    setMessage("");
    try {
      const data = await getStoreCustomer(customerKey);
      setCustomer(data.Customer);
      setReservations(data.Reservations || []);
      setOrders(data.Orders || []);
      setOwnedProducts(data.OwnedProducts || []);
      setServiceRecords(data.ServiceRecords || []);
      setInteractions(data.Interactions || []);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "客戶詳情載入失敗。");
      if (error instanceof StoreApiError && error.status === 401) navigate("/store-login", { replace: true });
    } finally {
      setDetailLoading(false);
    }
  }

  useEffect(() => {
    loadList(null, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerKey]);

  async function handleLogout() {
    await logoutStore().catch(() => undefined);
    navigate("/store-login", { replace: true });
  }

  function resetDetailState(locationId?: string) {
    setCustomer(null);
    setReservations([]);
    setOrders([]);
    setOwnedProducts([]);
    setServiceRecords([]);
    setInteractions([]);
    setBody("");
    setNextFollowUpAt("");
    setCustomerFormOpen(false);
    setCustomerFormMode("create");
    setCustomerForm({ ...emptyCustomerForm, LocationId: locationId || "" });
  }

  function handleLocationChange(locationId: string) {
    setCurrentLocationId(locationId);
    resetDetailState(locationId);
    navigate("/store/customers");
    loadList(locationId, 1);
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    loadList(effectiveLocationId, 1);
  }

  function openCreateCustomer() {
    setCustomerFormMode("create");
    setCustomerForm({ ...emptyCustomerForm, LocationId: effectiveLocationId || "" });
    setCustomerFormOpen(true);
    setMessage("");
  }

  function openEditCustomer() {
    if (!customer) return;
    setCustomerFormMode("edit");
    setCustomerForm({
      LocationId: customer.PreferredLocationId || customer.LastLocationId || effectiveLocationId || "",
      Name: customer.Name || "",
      Phone: customer.Phone || "",
      Email: customer.Email || "",
      Gender: customer.Gender || "",
      Birthday: customer.Birthday || "",
      AddressCity: customer.AddressCity || "",
      AddressDistrict: customer.AddressDistrict || "",
      AddressPostalCode: customer.AddressPostalCode || "",
      AddressLine: customer.AddressLine || "",
      Note: customer.Note || "",
    });
    setCustomerFormOpen(true);
    setMessage("");
  }

  function updateCustomerForm<K extends keyof StoreCustomerPayload>(key: K, value: StoreCustomerPayload[K]) {
    setCustomerForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSaveCustomer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCustomerSaving(true);
    setMessage("");
    try {
      const payload = { ...customerForm, LocationId: customerForm.LocationId || effectiveLocationId || "" };
      const data = customerFormMode === "edit" && customerKey ? await updateStoreCustomer(customerKey, payload) : await createStoreCustomer(payload);
      setMessage(data.message || (customerFormMode === "edit" ? "客戶主檔已更新。" : "客戶主檔已建立。"));
      setCustomerFormOpen(false);
      await loadList(payload.LocationId, 1);
      navigate(`/store/customers/${encodeURIComponent(data.Customer.CustomerKey)}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "客戶主檔儲存失敗。");
    } finally {
      setCustomerSaving(false);
    }
  }

  async function handleAddInteraction(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!customer) return;
    setSaving(true);
    setMessage("");
    try {
      await addStoreCustomerInteraction(customer.CustomerKey, { Body: body, NextFollowUpAt: nextFollowUpAt, LocationId: effectiveLocationId });
      setBody("");
      setNextFollowUpAt("");
      await loadDetail();
      await loadList(effectiveLocationId, pagination.Page);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "互動紀錄新增失敗。");
    } finally {
      setSaving(false);
    }
  }

  return (
    <StoreLayout currentLocationId={effectiveLocationId} onLocationChange={handleLocationChange} onLogout={handleLogout} title="客戶查詢" user={user}>
      <div className="space-y-4">
        <form className="grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 sm:grid-cols-2 2xl:grid-cols-[1fr_auto_auto_auto] 2xl:items-end" onSubmit={handleSearch}>
          <div className="sm:col-span-2 2xl:col-span-1">
            <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor="store-customer-keyword">客戶關鍵字</label>
            <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="store-customer-keyword" placeholder="姓名或電話，建議至少輸入 2 個字" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
            <p className="mt-1 text-xs font-bold leading-5 text-[#7b877f]">目前查詢本門市已關聯客戶；若客戶在現場但查不到，請用「新增客戶」輸入電話，系統會自動比對既有主檔並加入本門市。</p>
          </div>
          <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white" onClick={openCreateCustomer} type="button">
            <Plus size={18} />
            新增客戶
          </button>
          <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white" type="submit">
            <Search size={18} />
            查詢
          </button>
          <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => loadList(effectiveLocationId, pagination.Page)} type="button">
            <RefreshCw size={18} />
            重新整理
          </button>
        </form>

        {message && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{message}</div>}

        {customerFormOpen && (
          <form className="rounded-md border border-[#bfd8c8] bg-[#f7fbf8] p-4 shadow-[0_10px_30px_rgba(32,61,50,.06)]" onSubmit={handleSaveCustomer}>
            <div className="mb-4 flex flex-col gap-3 border-b border-[#dfe6e1] pb-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[.18em] text-[#7b877f]">Customer Master</p>
                <h2 className="mt-1 text-xl font-extrabold text-[#203d32]">{customerFormMode === "edit" ? "編輯客戶主檔" : "新增客戶主檔"}</h2>
                <p className="mt-1 text-sm leading-6 text-[#59665f]">先以電話查重並建立主檔；若電話或 Email 已存在，系統會加入目前門市關聯，不會重複建檔。</p>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
                <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => setCustomerFormOpen(false)} type="button">
                  <X size={18} />取消
                </button>
                <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white disabled:opacity-60" disabled={customerSaving} type="submit">
                  <Save size={18} />{customerSaving ? "儲存中" : "儲存客戶"}
                </button>
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <SelectField label="所屬門市" value={customerForm.LocationId} onChange={(value) => updateCustomerForm("LocationId", value)}>
                {(user?.Locations || []).map((item) => <option key={item.LocationId} value={item.LocationId}>{item.Name}</option>)}
              </SelectField>
              <InputField label="客戶姓名" required value={customerForm.Name} onChange={(value) => updateCustomerForm("Name", value)} />
              <InputField label="手機 / 電話" required value={customerForm.Phone} onChange={(value) => updateCustomerForm("Phone", value)} />
              <InputField label="Email" type="email" value={customerForm.Email || ""} onChange={(value) => updateCustomerForm("Email", value)} />
              <SelectField label="性別" value={customerForm.Gender || ""} onChange={(value) => updateCustomerForm("Gender", value)}>
                <option value="">未指定</option>
                <option value="Female">女</option>
                <option value="Male">男</option>
                <option value="Other">其他</option>
              </SelectField>
              <InputField label="生日" type="date" value={customerForm.Birthday || ""} onChange={(value) => updateCustomerForm("Birthday", value)} />
              <InputField label="縣市" value={customerForm.AddressCity || ""} onChange={(value) => updateCustomerForm("AddressCity", value)} />
              <InputField label="區域" value={customerForm.AddressDistrict || ""} onChange={(value) => updateCustomerForm("AddressDistrict", value)} />
              <InputField label="郵遞區號" value={customerForm.AddressPostalCode || ""} onChange={(value) => updateCustomerForm("AddressPostalCode", value)} />
              <div className="md:col-span-2 xl:col-span-3">
                <InputField label="地址" value={customerForm.AddressLine || ""} onChange={(value) => updateCustomerForm("AddressLine", value)} />
              </div>
              <div className="md:col-span-2 xl:col-span-4">
                <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor="store-customer-note">門市備註</label>
                <textarea id="store-customer-note" className="min-h-[92px] w-full rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="例如：聯絡偏好、家人陪同、配戴習慣、注意事項。" value={customerForm.Note || ""} onChange={(event) => updateCustomerForm("Note", event.target.value)} />
              </div>
            </div>
            {(customerForm.Phone.trim().length >= 2 || customerForm.Name.trim().length >= 2) && customers.length > 0 && customerFormMode === "create" && (
              <div className="mt-4 rounded-md border border-[#eed9aa] bg-[#fffaf0] p-3 text-sm text-[#745100]">
                <p className="font-extrabold">建檔前請確認是否為既有客戶</p>
                <div className="mt-2 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                  {customers.slice(0, 3).map((item) => (
                    <button className="rounded-md border border-[#f0d9a7] bg-white px-3 py-2 text-left hover:border-[#c99a39]" key={item.CustomerKey} onClick={() => navigate(`/store/customers/${encodeURIComponent(item.CustomerKey)}`)} type="button">
                      <span className="block font-extrabold text-[#203d32]">{item.Name}</span>
                      <span className="text-xs font-bold text-[#7b877f]">{item.Phone} / {item.IsMember ? "系統會員" : "門市客戶"}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </form>
        )}

        <div className="grid gap-4 2xl:grid-cols-[minmax(340px,440px)_minmax(0,1fr)]">
          <section className="min-w-0 rounded-md border border-[#dfe6e1] bg-white p-3">
            <div className="mb-3 flex items-center justify-between px-1">
              <div>
                <h2 className="font-extrabold text-[#203d32]">客戶列表</h2>
                <p className="mt-1 text-sm text-[#7b877f]">共 {pagination.Total} 位</p>
              </div>
              <UserRound className="text-[#315746]" size={20} />
            </div>
            {loading ? (
              <div className="rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 py-10 text-center text-sm text-[#7b877f]">載入中...</div>
            ) : customers.length === 0 ? (
              <div className="rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 py-10 text-center text-sm text-[#7b877f]">目前沒有符合條件的客戶。</div>
            ) : (
              <div className="grid gap-3">
                {customers.map((item) => <CustomerCard item={item} key={item.CustomerKey} selected={item.CustomerKey === customerKey} />)}
              </div>
            )}
            <div className="mt-4 flex flex-col gap-2 border-t border-[#edf0ee] pt-3 sm:flex-row sm:items-center sm:justify-between">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-50" disabled={pagination.Page <= 1 || loading} onClick={() => loadList(effectiveLocationId, pagination.Page - 1)} type="button">上一頁</button>
              <span className="text-center text-sm font-bold text-[#59665f]">{pagination.Page} / {pagination.TotalPages}</span>
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-50" disabled={pagination.Page >= pagination.TotalPages || loading} onClick={() => loadList(effectiveLocationId, pagination.Page + 1)} type="button">下一頁</button>
            </div>
          </section>

          <section className="min-w-0 rounded-md border border-[#dfe6e1] bg-white p-4">
            {!customerKey ? (
              <div className="flex min-h-[420px] flex-col items-center justify-center rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 text-center">
                <Search className="mb-3 text-[#315746]" size={34} />
                <h2 className="text-xl font-extrabold text-[#203d32]">選擇一位客戶查看詳情</h2>
                <p className="mt-2 max-w-[420px] text-sm leading-6 text-[#7b877f]">可查看客戶在本門市範圍內的預約、訂單、互動紀錄，並建立下一次回訪。</p>
              </div>
            ) : detailLoading ? (
              <div className="min-h-[420px] rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 py-10 text-center text-sm text-[#7b877f]">詳情載入中...</div>
            ) : customer ? (
              <div className="space-y-4">
                <div className="flex flex-col gap-3 border-b border-[#edf0ee] pb-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-[#e9f4ec] px-2.5 py-1 text-xs font-bold text-[#245438]">{customer.IsMember ? "會員" : "訪客"}</span>
                      <span className="rounded-full bg-[#f4f6f5] px-2.5 py-1 text-xs font-bold text-[#59665f]">{customer.LastLocationName || "門市客戶"}</span>
                    </div>
                    <h2 className="text-2xl font-extrabold text-[#203d32]">{customer.Name}</h2>
                    <p className="mt-1 text-sm text-[#59665f]">{customer.Phone}</p>
                  </div>
                  <div className="rwd-action-grid lg:grid-cols-3 2xl:flex 2xl:flex-wrap 2xl:justify-end">
                    <ActionButton onClick={() => copyPhoneNumber(customer.Phone)}><Copy size={17} />複製電話</ActionButton>
                    <ActionButton onClick={openEditCustomer} variant="secondary"><Edit3 size={17} />編輯主檔</ActionButton>
                    <ActionButton href={`/store/service-records?Keyword=${encodeURIComponent(customer.Phone)}`} variant="secondary"><FileText size={17} />服務紀錄</ActionButton>
                    <ActionButton href={`/store/orders?Keyword=${encodeURIComponent(customer.Phone)}&Status=All`} variant="secondary"><ShoppingBag size={17} />門市訂單</ActionButton>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-[minmax(0,1fr)_minmax(260px,1.45fr)_minmax(0,1fr)_minmax(0,1fr)]">
                  <InfoTile label="客戶編號" value={customer.CustomerCode || "-"} />
                  <InfoTile icon={<Phone size={13} />} label="電話" value={customer.Phone} />
                  <InfoTile icon={<Mail size={13} />} label="Email" value={customer.Email || "-"} />
                  <InfoTile label="性別 / 生日" value={`${genderLabels[customer.Gender || ""] || "未指定"} / ${customer.Birthday || "-"}`} />
                  <InfoTile label="地址" value={[customer.AddressPostalCode, customer.AddressCity, customer.AddressDistrict, customer.AddressLine].filter(Boolean).join("") || "-"} />
                  <InfoTile icon={<CalendarClock size={13} />} label="預約" value={`${customer.ReservationCount} 筆`} />
                  <InfoTile icon={<ShoppingBag size={13} />} label="訂單" value={`${customer.OrderCount} 筆`} />
                  <InfoTile label="來源 / 狀態" value={`${customer.SourceType || "Manual"} / ${customer.Status || "Active"}`} />
                </div>
                {customer.IsBlacklisted && <div className="rounded-md border border-[#e8c8c5] bg-[#fff5f4] px-4 py-3 text-sm font-bold text-[#9a2d2d]">此客戶被標記為注意/黑名單，請依總管理端指示處理。</div>}
                {customer.Note && <TextBlock title="門市備註" text={customer.Note} />}

                <section className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4">
                  <h3 className="mb-3 flex items-center gap-2 font-extrabold text-[#203d32]"><AlertCircle size={18} />客戶待處理摘要</h3>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <InfoTile label="尾款待收" value={money(customer.Stats?.BalanceAmount)} tone={Number(customer.Stats?.BalanceAmount || 0) > 0 ? "notice" : "default"} />
                    <InfoTile label="待收訂單" value={`${customer.Stats?.BalanceDueOrders || 0} 筆`} />
                    <InfoTile label="服務紀錄" value={`${customer.Stats?.ActiveServiceRecords || serviceRecords.length} 筆`} />
                    <InfoTile label="最近 / 下次" value={`${customer.Stats?.LastServiceDate || "無服務日"} / ${customer.Stats?.NextFollowUpAt || "無回訪"}`} />
                  </div>
                </section>

                <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                  <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="flex items-center gap-2 font-extrabold text-[#203d32]"><Headphones size={18} />客戶設備履歷</h3>
                    <span className="text-xs font-bold text-[#7b877f]">完成交付結單後自動建立</span>
                  </div>
                  {ownedProducts.length === 0 ? <EmptyText text="尚無已交付設備履歷。" /> : <div className="space-y-3">{ownedProducts.map((item) => <OwnedProductRow item={item} key={item.CustomerOwnedProductId} />)}</div>}
                </section>

                <form className="rounded-md border border-[#dfe6e1] bg-white p-4" onSubmit={handleAddInteraction}>
                  <div className="mb-3 flex items-center gap-2 font-extrabold text-[#203d32]">
                    <MessageSquarePlus size={18} />
                    新增互動 / 回訪
                  </div>
                  <textarea className="min-h-[112px] w-full rounded-md border border-[#cfd8d2] p-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="例如：客戶詢問試戴方案，約週三回電。" value={body} onChange={(event) => setBody(event.target.value)} />
                  <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
                    <div>
                      <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor="customer-follow-up">下次回訪時間</label>
                      <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="customer-follow-up" type="datetime-local" value={nextFollowUpAt} onChange={(event) => setNextFollowUpAt(event.target.value)} />
                    </div>
                    <button className="inline-flex h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={saving} type="submit">{saving ? "儲存中" : "儲存互動"}</button>
                  </div>
                </form>

                <div className="grid gap-4 xl:grid-cols-2">
                  <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                    <h3 className="mb-3 flex items-center gap-2 font-extrabold text-[#203d32]"><Headphones size={18} />最近服務紀錄</h3>
                    {serviceRecords.length === 0 ? <EmptyText text="尚無服務紀錄。" /> : <div className="space-y-3">{serviceRecords.map((item) => <ServiceRecordRow item={item} key={item.ServiceRecordId} />)}</div>}
                  </section>
                  <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                    <h3 className="mb-3 flex items-center gap-2 font-extrabold text-[#203d32]"><ClipboardList size={18} />預約歷程</h3>
                    {reservations.length === 0 ? <EmptyText text="尚無預約紀錄。" /> : <div className="space-y-3">{reservations.map((item) => <ReservationRow item={item} key={`${item.SourceType}-${item.SourceId}`} />)}</div>}
                  </section>
                </div>
                <div className="grid gap-4 xl:grid-cols-2">
                  <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                    <h3 className="mb-3 flex items-center gap-2 font-extrabold text-[#203d32]"><ShoppingBag size={18} />訂單摘要</h3>
                    {orders.length === 0 ? <EmptyText text="尚無門市取貨訂單。" /> : <div className="space-y-3">{orders.map((item) => <OrderRow item={item} key={item.OrderId} />)}</div>}
                  </section>
                  <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                    <h3 className="mb-3 flex items-center gap-2 font-extrabold text-[#203d32]"><MessageSquarePlus size={18} />互動紀錄</h3>
                    {interactions.length === 0 ? (
                      <EmptyText text="尚無互動紀錄。" />
                    ) : (
                      <div className="space-y-3">
                        {interactions.map((item) => (
                          <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3" key={item.InteractionId}>
                            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                              <span className="text-sm font-extrabold text-[#315746]">{item.InteractionType === "FollowUp" ? "回訪" : "備註"}</span>
                              <span className="text-xs font-bold text-[#7b877f]">{item.CreatedAt}</span>
                            </div>
                            <p className="text-sm leading-6 text-[#59665f]">{item.Body || "-"}</p>
                            {item.NextFollowUpAt && <p className="mt-2 text-xs font-bold text-[#8a6a2f]">下次回訪：{item.NextFollowUpAt}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                </div>
              </div>
            ) : (
              <EmptyText text="找不到客戶詳情。" />
            )}
          </section>
        </div>
      </div>
    </StoreLayout>
  );
}

function EmptyText({ text }: { text: string }) {
  return <div className="rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 py-8 text-center text-sm text-[#7b877f]">{text}</div>;
}

function InputField({ label, onChange, required = false, type = "text", value }: { label: string; onChange: (value: string) => void; required?: boolean; type?: string; value: string }) {
  const id = `customer-field-${label}`;
  return (
    <div className="min-w-0">
      <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor={id}>{label}{required ? " *" : ""}</label>
      <input
        className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
        id={id}
        required={required}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function SelectField({ children, label, onChange, value }: { children: ReactNode; label: string; onChange: (value: string) => void; value: string }) {
  const id = `customer-select-${label}`;
  return (
    <div className="min-w-0">
      <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor={id}>{label}</label>
      <select
        className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {children}
      </select>
    </div>
  );
}

function TextBlock({ text, title }: { text?: string | null; title: string }) {
  if (!text) return null;
  return (
    <section className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4">
      <h3 className="mb-2 font-extrabold text-[#203d32]">{title}</h3>
      <p className="whitespace-pre-wrap break-words text-sm leading-6 text-[#59665f]">{text}</p>
    </section>
  );
}
